import { createContext, ReactNode, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router";
import ActiveAccount from "../classes/activeAccount";
import CurrentUser from "../classes/currentUser";
import { useOnBehalfOfValue } from "../recoil/admin/hooks";
import { AccessRoleType } from "../types/access";
import { AccountModel } from "../types/account";
import { ApiResponse } from "../types/api";
import { InvitationModel } from "../types/invitation";
import { UserModel, UserStatusEnum } from "../types/user";
import { useApi } from "./ApiContext";
import useAuth from "./AuthContext";
import useToast from "./ToastContext";

const AUTHENTICATED_ROUTES = ["account", "accounts"];

export type UserContextType = {
	user: UserModel | undefined;
	isAccountOwner: boolean;
	activeAccount: ActiveAccount | undefined;
	isLoading: boolean;
	refetchUser: () => Promise<void>;
	updateUser: (user: Partial<UserModel>) => Promise<Response | undefined>;
	considerTerms: (accept: boolean) => Promise<void>;
	changeUserLanguage: (languageCode: string) => void;

	// Create/update accounts
	updatePersonalAccount: (account: AccountModel) => Promise<void>;
	createPersonalAccount: (account: AccountModel) => Promise<string | undefined>;
	updateCompanyAccount: (account: AccountModel) => Promise<string | undefined>;
	createCompanyAccount: (account: AccountModel) => Promise<string | undefined>;
	deleteAccount: () => Promise<void>;

	// Invitations
	getInvitations: () => Promise<ApiResponse<InvitationModel[]>>;
	inviteUserToAccount: (phoneNumber: string) => Promise<boolean>;
	revokeAccountInvitation: (phoneNumber: string) => Promise<boolean>;
	acceptInvite: (accountId: string) => Promise<boolean>;
	declineInvite: (accountId: string) => Promise<boolean>;
};

export const UserContext = createContext<UserContextType | undefined>(undefined);
UserContext.displayName = "UserContext";

export function UserContextProvider({ children }: { children: ReactNode | ReactNode[] }) {
	const location = useLocation();
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const toast = useToast();
	const api = useApi();
	const { currentUserClaims, switchAccount } = useAuth();
	const onBehalfOf = useOnBehalfOfValue();

	const {
		data: user,
		isLoading: isFetching,
		refetch,
	} = useQuery("profile", async () => {
		const response = await api.auth.getProfile();
		return response.result;
	});

	const activeAccount = useMemo(() => {
		const accountId = currentUserClaims?.accountId;
		if (!accountId || !user) return;

		const account = user.accounts.find((a) => a.id === accountId);
		if (account) {
			return new ActiveAccount(account, account.type, account?.access.role == AccessRoleType.Owner, account.name);
		}
	}, [currentUserClaims?.accountId, user]);

	useEffect(
		function redirect() {
			if (!user) return;

			if (!onBehalfOf && !user.termsAccepted && AUTHENTICATED_ROUTES.includes(location.pathname.split("/")[1])) {
				navigate("/terms", { replace: true });
				return;
			}

			if (
				user.active === UserStatusEnum.Pending &&
				AUTHENTICATED_ROUTES.includes(location.pathname.split("/")[1])
			) {
				navigate("/register/profile", { replace: true });
				return;
			}
		},
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
		[user, location]
	);

	useEffect(
		function syncBrowserLanguageToStoredUserPreference() {
			const storedUserLanguage = user?.languageCode;
			const storedLanguageToLanguageCode = storedUserLanguage?.toUpperCase() === "NO" ? "nb-NO" : "en";
			if (storedUserLanguage && i18n.language !== storedLanguageToLanguageCode) {
				i18n.changeLanguage(storedLanguageToLanguageCode);
			}
		},
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
		[user?.languageCode]
	);

	async function changeUserLanguage(languageCode: string) {
		if (!user) {
			return;
		}
		await api.auth.updateUserInfo({
			...user,
			languageCode,
		});
		i18n.changeLanguage(languageCode);
	}

	/* --- USER --- */

	async function refetchUser() {
		await refetch();
	}

	async function updateUser(updatedUser: Partial<UserModel>) {
		if (!user) {
			return;
		}

		return await api.auth.updateUserInfo({
			...user,
			...updatedUser,
		});
	}

	async function considerTerms(termsAccepted: boolean) {
		await api.auth.updateTerms(termsAccepted);
		await refetchUser();
	}

	/* --- ACCOUNT --- */

	async function updatePersonalAccount(account: AccountModel) {
		if (!user || !activeAccount?.isAccountOwner) return;

		await api.account.updatePersonalAccount(account, user);
		await refetchUser();
	}

	async function updateCompanyAccount(account: AccountModel) {
		if (!user || !activeAccount?.isAccountOwner) return;

		const response = await api.account.updateCompanyAccount(account);
		if (response.isSuccess) {
			await refetchUser();
			return response.result?.id;
		} else throw Error(response.message);
	}

	async function createPersonalAccount(account: AccountModel) {
		if (!user) return;

		const response = await api.account.createPersonalAccount(account, user);
		if (response.isSuccess) {
			await refetchUser();
			return response.result?.id;
		} else throw Error(response.message);
	}

	async function createCompanyAccount(account: AccountModel): Promise<string | undefined> {
		const response = await api.account.createCompanyAccount(account);
		if (response.isSuccess) {
			await refetchUser();
			return response.result?.id;
		} else throw Error(response.message);
	}

	async function deleteAccount(): Promise<void> {
		const accountToSwitchTo = user?.accounts.filter((a) => a.id !== activeAccount?.accountId)[0];

		await api.account.deleteAccount().then(() => {
			if (accountToSwitchTo) {
				switchAccount(accountToSwitchTo.id);
			}
		});
		await refetchUser();
	}

	async function getInvitations(): Promise<ApiResponse<InvitationModel[]>> {
		return await api.account.getInvitations();
	}

	async function inviteUserToAccount(phoneNumber: string): Promise<boolean> {
		if (!activeAccount?.isAccountOwner) {
			throw new Error("Only the owner of an account can invite additional users");
		}

		const response = await api.account.inviteUserToActiveAccount(phoneNumber);
		if (response.isSuccess) {
			toast.toastSuccess(t("usersPage.userInvited", { phoneNumber }));
			return true;
		} else {
			toast.toastError(t("usersPage.couldNotInvite", { phoneNumber }));
			return false;
		}
	}

	async function revokeAccountInvitation(phoneNumber: string): Promise<boolean> {
		if (!activeAccount) {
			return false;
		}

		if (activeAccount.account?.access.role !== AccessRoleType.Owner) {
			throw new Error("Only the owner of an account can revoke an invitation");
		}

		const response = await api.account.revokeAccountInvitation(activeAccount.accountId, phoneNumber);
		if (response.isSuccess) {
			toast.toastSuccess(t("usersPage.invitationRevoked", { phoneNumber }));
			return true;
		} else {
			toast.toastError(t("usersPage.couldNotRevoke"));
			return false;
		}
	}

	async function acceptInvite(invitationId: string): Promise<boolean> {
		const response = await api.auth.acceptInvite(invitationId);
		if (!response.isSuccess) {
			toast.toastError(t("accountsPage.acceptInviteFailed"));
			return false;
		}
		await refetch();
		return true;
	}

	async function declineInvite(invitationId: string) {
		const response = await api.auth.declineInvite(invitationId);
		if (!response.isSuccess) {
			toast.toastError(t("accountsPage.declineInviteFailed"));
			return false;
		}
		await refetch();
		return true;
	}

	return (
		<UserContext.Provider
			value={{
				user,
				isAccountOwner: currentUserClaims ? new CurrentUser(currentUserClaims as any).isOwner : false, // hotfix, will rewrite currentUserClaims to be a instance of CurrentUser class later.
				isLoading: isFetching,
				refetchUser,
				updateUser,
				considerTerms,
				changeUserLanguage,
				activeAccount,
				updatePersonalAccount,
				updateCompanyAccount,
				createPersonalAccount,
				createCompanyAccount,
				deleteAccount,
				getInvitations,
				inviteUserToAccount,
				revokeAccountInvitation,
				acceptInvite,
				declineInvite,
			}}
		>
			{children}
		</UserContext.Provider>
	);
}

function useUserContext() {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error("useUserContext() must be used inside a UserContext provider");
	}
	return context;
}

export function useMaybeUserContext() {
	return useContext(UserContext);
}

export default useUserContext;
